import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { AgentService } from "@/services/agent.service"
import { zodResolver } from "@hookform/resolvers/zod"
import { IconPlus } from "@tabler/icons-react"
import { useNavigate } from "@tanstack/react-router"
import { useState } from "react"
import { useForm } from "react-hook-form"
import { toast } from "sonner"
import { z } from "zod"
import { defaultAgentConfig } from "../schema"

const createAgentSchema = z.object({
  name: z.string().min(1, "O nome é obrigatório"),
})

type CreateAgentForm = z.infer<typeof createAgentSchema>

export function CreateAgentDialog() {
  const [open, setOpen] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const navigate = useNavigate()

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<CreateAgentForm>({
    resolver: zodResolver(createAgentSchema),
  })

  const onSubmit = async (data: CreateAgentForm) => {
    setIsSubmitting(true)
    try {
      const newAgent = await AgentService.create({
        ...defaultAgentConfig,
        name: data.name,
        systemPrompt: "Você é um assistente virtual inteligente e prestativo.", // Default prompt to satisfy min length
      })

      toast.success("Agente criado com sucesso!")
      setOpen(false)
      reset()
      navigate({ to: `/agents/${newAgent.id}/edit` })
    } catch (error) {
      console.error(error)
      toast.error("Erro ao criar agente. Tente novamente.")
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button>
          <IconPlus className="w-4 h-4 mr-2" />
          Novo Agente
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Criar Novo Agente</DialogTitle>
          <DialogDescription>
            Dê um nome para o seu novo agente de inteligência artificial.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="grid gap-4 py-4">
            <div className="flex flex-col gap-2">
              <Label htmlFor="name" className="text-left">
                Nome do Agente
              </Label>
              <Input
                id="name"
                placeholder="Ex: Atendente Virtual"
                {...register("name")}
              />
              {errors.name && (
                <span className="text-sm text-red-500">
                  {errors.name.message}
                </span>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? "Criando..." : "Criar Agente"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
